"use client";

import type { CSSProperties, ReactNode } from "react";

export type ToastStyle = "neutral" | "success" | "info" | "warning" | "error";
export type ToastPlacement = "topStack" | "centeredText";

export interface ToastAction {
	readonly label: ReactNode;
	readonly onPress?: () => void;
}

export interface ToastProps {
	readonly children?: ReactNode;
	readonly title?: ReactNode;
	readonly message?: ReactNode;
	readonly style?: ToastStyle;
	/** @deprecated use `style` instead. */
	readonly tone?: ToastStyle;
	readonly placement?: ToastPlacement;
	readonly action?: ToastAction;
	readonly onClose?: () => void;
	readonly className?: string;
}

function toastColors(variant: ToastStyle): {
	color: string;
	border: string;
	background: string;
} {
	switch (variant) {
		case "success":
			return {
				color: "var(--ds-text-primary)",
				border: "color-mix(in srgb, var(--ds-accent) 50%, transparent)",
				background: "var(--ds-glass)",
			};
		case "error":
			return {
				color: "#ff8a84",
				border: "color-mix(in srgb, var(--ds-destructive) 50%, transparent)",
				background: "var(--ds-glass)",
			};
		case "warning":
			return {
				color: "color-mix(in srgb, orange 80%, var(--ds-text-primary))",
				border: "color-mix(in srgb, orange 45%, transparent)",
				background: "var(--ds-glass)",
			};
		case "info":
			return {
				color: "var(--ds-accent)",
				border: "color-mix(in srgb, var(--ds-accent) 45%, transparent)",
				background: "var(--ds-glass)",
			};
		default:
			return {
				color: "var(--ds-text-primary)",
				border: "var(--ds-glass-stroke)",
				background: "var(--ds-glass)",
			};
	}
}

/**
 * Toast — app-level notification surface. Renders a single toast with a style
 * (neutral/success/info/warning/error) and placement (topStack/centeredText).
 * Mirrors `DSToast` on iOS. The `ToastHost` renders these via `ToastCenter`.
 */
export function Toast({
	children,
	title,
	message,
	style: variant = "neutral",
	tone,
	placement = "topStack",
	action,
	onClose,
	className = "",
}: Readonly<ToastProps>) {
	const resolved = variant ?? tone ?? "neutral";
	const colors = toastColors(resolved);
	const placementStyle: CSSProperties =
		placement === "centeredText"
			? { textAlign: "center", justifyContent: "center" }
			: { textAlign: "left" };
	return (
		<div
			className={`ds-toast ds-toast--${resolved} ${className}`.trim()}
			role={resolved === "error" ? "alert" : "status"}
			style={{
				color: colors.color,
				border: `1px solid ${colors.border}`,
				background: colors.background,
				...placementStyle,
			}}
		>
			{title === undefined ? null : <strong>{title}</strong>}
			{message === undefined ? null : (
				<p style={{ margin: "4px 0 0", color: "var(--ds-text-secondary)" }}>
					{message}
				</p>
			)}
			{children === undefined ? null : <span>{children}</span>}
			{action === undefined && onClose === undefined ? null : (
				<span
					style={{
						display: "flex",
						gap: "var(--ds-space-sm)",
						marginLeft: "auto",
					}}
				>
					{action === undefined ? null : (
						<button
							type="button"
							onClick={action.onPress}
							style={{
								border: 0,
								background: "none",
								color: colors.color,
								font: "inherit",
								fontWeight: 600,
								cursor: "pointer",
							}}
						>
							{action.label}
						</button>
					)}
					{onClose === undefined ? null : (
						<button
							type="button"
							aria-label="关闭"
							onClick={onClose}
							style={{
								border: 0,
								background: "none",
								color: "var(--ds-text-secondary)",
								cursor: "pointer",
							}}
						>
							×
						</button>
					)}
				</span>
			)}
		</div>
	);
}
